import * as React from "react";
import { MarketplaceReleaseInstance, ReleaseRevision } from "../types/release";
import { NGFormData } from "../../../mobxStores/ngFormData";
import { BasePathContext } from "../../../reactContexts/basePathContext";
import { useNavigate } from "react-router-dom";
import { Template } from "../types/template";
import { request, resourceLink } from "../../../services/cpln";
import { ConsoleContext } from "../../../mobxStores/consoleContext/consoleContext";
import { Modal, notification } from "antd";
import jsYaml from "js-yaml";
import { Loader } from "../../../components/layout/loader";
import { NGFormContext } from "../../../reactContexts/ngFormContext";
import { NGFormLabel } from "../../../newcomponents/text/formLabel";
import { NGLabel } from "../../../newcomponents/text/label";
import { CodeEditor } from "../../group/identityMatcher/codeEditor";
import { NGButton } from "../../../newcomponents/button/Button";
import { Copy, Download } from "react-feather";
import { k8sKeySort } from "../../../services/utils";
import { useDetailContext } from "../../../components/detail/detailContext";
import { decodeRevisionFrom, getMarketplaceErrorMessage, getTemplateVersion, getVersions } from "../utils";
import {
  DOCS_URL,
  MARKETPLACE_BRANCH,
  MARKETPLACE_SERVICE_URL,
  MARKETPLACE_TEMPLATE_REPO,
} from "../../../envVariables";
import { FilesReducerActionType, useFilesReducer } from "../template/filesReducer";
import { CodeEditorModal } from "../../../components/modals/codeEditorModal";
import { isEqual } from "lodash";
import { CodeSnippet } from "../../../components/generic/codeSnippet/codeSnippet";
import { NGSelect } from "../../../newcomponents/select/ngselect";
import NGAlert from "../../../newcomponents/alert";

interface Props {
  template: Template;
  release: MarketplaceReleaseInstance;
  currentRevisionSecretName: string;
}
export const ReleaseUpgrade: React.FC<Props> = ({ template, release, currentRevisionSecretName }) => {
  const formDataRef = React.useRef(new NGFormData());

  const { fetchItem } = useDetailContext();

  const basePath = React.useContext(BasePathContext);
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = React.useState(true);
  const [isLoadingTemplate, setIsLoadingTemplate] = React.useState(false);
  const [isLoadingUpgrade, setIsLoadingUpgrade] = React.useState(false);

  const [revision, setRevision] = React.useState<ReleaseRevision>(null as any);
  const versions = getVersions(template);

  // default means the default in the template version
  const [currentVersionDefaultValues, setCurrentVersionDefaultValues] = React.useState("");
  // initial means the values that user put in for the applied release
  const [initialValues, setInitialValues] = React.useState("");
  // upgrade version means what the user has selected, used when version is different from the released one
  const [upgradeVersionDefaultValues, setUpgradeVersionDefaultValues] = React.useState("");
  // values means what the user is typing now
  const [values, setValues] = React.useState("");

  const [isTemplateView, setIsTemplateView] = React.useState(false);
  const [helmTemplates, setHelmTemplates] = React.useState<any[]>([]);

  const [currentVersionDefaultFilesState, currentVersionDefaultFilesDispatch] = useFilesReducer();
  const [initialFilesState, initialFilesDispatch] = useFilesReducer();

  const [upgradeVersionDefaultFilesState, upgradeVersionDefaultFilesDispatch] = useFilesReducer();
  const [filesState, filesDispatch] = useFilesReducer();

  const [codeEditor, setCodeEditor] = React.useState<{ readonly: boolean; property: string } | null>(null);
  const [upgradeVersion, setUpgradeVersion] = React.useState(release.version);

  React.useEffect(() => {
    fetchState();
  }, []);

  async function fetchState() {
    try {
      const { data } = await request({
        url: resourceLink("secret", currentRevisionSecretName) + "/-reveal",
      });
      setRevision(decodeRevisionFrom(data)!);
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
      let errorMessage = e.response?.data?.error;
      if (!errorMessage) errorMessage = e.message;
      notification.warning({ message: "Failed", description: errorMessage });
    }
  }

  React.useEffect(() => {
    processDefaults();
  }, [template, revision, upgradeVersion]);

  function processDefaults() {
    if (!revision) {
      return;
    }
    if (!versions.includes(release.version)) {
      return;
    }
    if (!versions.includes(upgradeVersion)) {
      return;
    }

    const currentVersionFileDefaults: { [_: string]: string } = {};
    for (const [property, value] of Object.entries(template.versions[release.version].files || {})) {
      currentVersionFileDefaults[property] = value.content;
    }

    currentVersionDefaultFilesDispatch({
      type: FilesReducerActionType.SET_DEFAULTS,
      payload: { content: "", property: "", defaults: currentVersionFileDefaults },
    });

    const values = template.versions[release.version].values;
    setCurrentVersionDefaultValues(values);

    // upgrade defaults only if upgrade version is different
    if (release.version === upgradeVersion) {
      processValues();
      return;
    }

    const upgradeVersionFileDefaults: { [_: string]: string } = {};
    for (const [property, value] of Object.entries(template.versions[upgradeVersion].files || {})) {
      upgradeVersionFileDefaults[property] = value.content;
    }

    upgradeVersionDefaultFilesDispatch({
      type: FilesReducerActionType.SET_DEFAULTS,
      payload: { content: "", property: "", defaults: upgradeVersionFileDefaults },
    });

    const upgradeValues = template.versions[upgradeVersion].values;
    setUpgradeVersionDefaultValues(upgradeValues);

    //
    processValues();
  }

  function processValues() {
    // set initial for current version and the state depending on the different of current version and upgrade version
    const currentTemplateVersion = getTemplateVersion(template, release.version);
    const currentValuesObj: any = jsYaml.load(revision.valuesFiles[0] || currentTemplateVersion?.values || "");
    const currentValuesObjWithoutFiles: any = jsYaml.load(
      revision.valuesFiles[0] || currentTemplateVersion?.values || "",
    );

    // files
    const currentVersionFileDefaults: { [_: string]: string } = {};
    for (const [property, value] of Object.entries(template.versions[release.version].files || {})) {
      currentVersionFileDefaults[property] = value.content;
    }

    const currentVersionFileInitials: { [_: string]: string } = {};
    for (const [property] of Object.entries(template.versions[release.version].files || {})) {
      const fileValueObj = currentValuesObj[property];
      const fileValueString = jsYaml.dump(fileValueObj);

      let content = fileValueString || currentVersionFileDefaults[property] || "";

      if (content.trimStart().startsWith("|-")) {
        // remove initial "|- "" and remove indent that was added manually
        content = content
          .slice(3)
          .split("\n")
          .map((l) => l.slice(2))
          .join("\n");
      }

      currentVersionFileInitials[property] = content;

      initialFilesDispatch({
        type: FilesReducerActionType.SET_FILE,
        payload: { content: content, property: property },
      });
      if (release.version === upgradeVersion) {
        filesDispatch({
          type: FilesReducerActionType.SET_FILE,
          payload: { content: content, property: property },
        });
      }
      delete currentValuesObjWithoutFiles[property];
    }

    initialFilesDispatch({
      type: FilesReducerActionType.SET_INITIALS,
      payload: { content: "", property: "", defaults: currentVersionFileInitials },
    });

    const valuesObjWithoutFilesString = jsYaml.dump(currentValuesObjWithoutFiles);

    setInitialValues(valuesObjWithoutFilesString);

    if (release.version === upgradeVersion) {
      setValues(valuesObjWithoutFilesString);
      filesDispatch({
        type: FilesReducerActionType.SET_INITIALS,
        payload: { content: "", property: "", defaults: currentVersionFileInitials },
      });
    } else {
      // set current values as the upgrade version values minus files, also set filesDispatch

      const upgradeTemplateVersion = getTemplateVersion(template, upgradeVersion);
      const upgradeValuesObj: any = jsYaml.load(upgradeTemplateVersion?.values || "");
      const upgradeValuesObjWithoutFiles: any = jsYaml.load(upgradeTemplateVersion?.values || "");

      // files
      const upgradeVersionFileInitials: { [_: string]: string } = {};
      for (const [property] of Object.entries(template.versions[upgradeVersion].files || {})) {
        const fileValueObj = upgradeValuesObj[property];
        const fileValueString = jsYaml.dump(fileValueObj);

        let content = fileValueString || "";

        if (content.trimStart().startsWith("|-")) {
          // remove initial "|- "" and remove indent that was added manually
          content = content
            .slice(3)
            .split("\n")
            .map((l) => l.slice(2))
            .join("\n");
        }

        upgradeVersionFileInitials[property] = content;

        filesDispatch({
          type: FilesReducerActionType.SET_FILE,
          payload: { content: content, property: property },
        });
        if (upgradeVersion === upgradeVersion) {
          filesDispatch({
            type: FilesReducerActionType.SET_FILE,
            payload: { content: content, property: property },
          });
        }
        delete upgradeValuesObjWithoutFiles[property];
      }

      filesDispatch({
        type: FilesReducerActionType.SET_INITIALS,
        payload: { content: "", property: "", defaults: upgradeVersionFileInitials },
      });

      const valuesObjWithoutFilesString = jsYaml.dump(upgradeValuesObjWithoutFiles);

      setValues(valuesObjWithoutFilesString);
    }
  }

  function getProcessedValues(): string {
    let processedValues = values;
    const templateVersion = getTemplateVersion(template, upgradeVersion);
    if (!templateVersion) {
      return processedValues;
    }
    if (templateVersion.files && Object.keys(templateVersion.files).length > 0) {
      for (let fileProperty of Object.keys(templateVersion.files)) {
        // TODO for handling multi files
        // const fileInfo = templateVersion.chart.files[fileProperty];
        const fileValue = filesState.current[fileProperty] || "";

        const indentCount = 2;
        const indentedFileValue = fileValue
          .split("\n")
          .map((line) => " ".repeat(indentCount) + line)
          .join("\n");

        processedValues += "\n" + fileProperty + ": |- \n" + indentedFileValue;
      }
    }
    return processedValues;
  }

  async function handleUpgrade() {
    try {
      setIsLoadingUpgrade(true);
      await request({
        service: "marketplace",
        method: "post",
        url: `/helm/install`,
        body: {
          org: release.org,
          gvc: release.gvc === " " ? "" : release.gvc,
          name: release.name,
          template: release.template,
          version: upgradeVersion,
          values: getProcessedValues(),
          action: "upgrade",
        },
      });
      notification.success({ message: "Success", description: "Upgraded template" });
      setIsLoadingUpgrade(false);
      navigate(basePath);
      if (fetchItem) {
        fetchItem();
      }
    } catch (e) {
      setIsLoadingUpgrade(false);
      const errorMessage = getMarketplaceErrorMessage(e);
      notification.warning({
        message: "Failed",
        description: (
          <div className="flex flex-col gap-2">
            {errorMessage.map((m) => (
              <div>{m}</div>
            ))}
          </div>
        ),
        duration: 20,
      });
      if (fetchItem) {
        fetchItem();
      }
    }
  }

  async function handleTemplate() {
    try {
      setIsLoadingTemplate(true);
      const { data } = await request({
        service: "marketplace",
        method: "post",
        url: `/helm/template`,
        body: {
          org: ConsoleContext.org,
          gvc: release.gvc === " " ? "" : release.gvc,
          name: release.name,
          template: template.name,
          version: upgradeVersion,
          values: getProcessedValues(),
        },
      });
      setHelmTemplates(jsYaml.loadAll(data.message));
      setIsTemplateView(true);
      notification.success({ message: "Success", description: "Templated" });
      setIsLoadingTemplate(false);
    } catch (e) {
      setIsLoadingTemplate(false);
      setHelmTemplates([]);
      const errorMessage = getMarketplaceErrorMessage(e);
      notification.warning({
        message: "Failed",
        description: (
          <div className="flex flex-col gap-2">
            {errorMessage.map((m) => (
              <div>{m}</div>
            ))}
          </div>
        ),
        duration: 20,
      });
    }
  }

  if (isLoading) {
    return (
      <div className="relative">
        <Loader
          reason="Fetching data"
          style={{
            width: 100,
            height: 100,
            margin: 0,
            left: `calc(50% - 50px)`,
            right: "unset",
            top: 150,
            bottom: "unset",
          }}
        />
      </div>
    );
  }

  let filesAreDirty = false;
  if (release.version !== upgradeVersion) {
    filesAreDirty = !isEqual(filesState.current, upgradeVersionDefaultFilesState.initials);
  } else {
    filesAreDirty = !isEqual(filesState.current, initialFilesState.initials);
  }

  const placeholder = [""];

  return (
    <NGFormContext.Provider value={formDataRef.current}>
      <div style={{ maxWidth: 900 }}>
        <div className="header-inputs">
          <div
            className="flex mb-4 px-4 py-1 rounded w-fit"
            style={{ backgroundColor: "white", color: "var(--color-gray-1600)" }}
          >
            <div style={{ width: 50 }} className="flex items-center">
              <img className="object-fit" src={`${MARKETPLACE_SERVICE_URL}/icon/${template.name}`} />
            </div>
            <div className="flex flex-col ml-4">
              <span className="leading-none text-lg font-semibold">{template.name}</span>

              <span className="leading-none text-sm">{getTemplateVersion(template, release.version)?.category}</span>
            </div>
          </div>
          <div className="grid grid-cols-4">
            {release.gvc !== " " ? (
              <div>
                <NGFormLabel>GVC</NGFormLabel>
                <div className="mb-4">{release.gvc}</div>
              </div>
            ) : null}
            <div>
              <NGFormLabel>Release Name</NGFormLabel>
              <div className="mb-4">{release.name}</div>
            </div>
            <div>
              <NGFormLabel>Released Version</NGFormLabel>
              <div className="mb-4">{release.version}</div>
            </div>
            <div>
              <NGFormLabel>Upgrade Version</NGFormLabel>
              {Object.keys(template.versions).length > 0 ? (
                <NGSelect
                  size={`small`}
                  value={upgradeVersion}
                  onChange={setUpgradeVersion}
                  options={Object.entries(template.versions).map(([key, value]) => ({ label: key, value: key }))}
                />
              ) : (
                <div className="mb-4">{release.version}</div>
              )}
            </div>
          </div>
        </div>
        {/* <div className="mb-8 flex gap-4">
          <div className="flex-grow text-xs" style={{ flexBasis: 1 }}>
            <div>Initial</div>
            <div>Values</div>
            <div>{initialValues}</div>
            <div>Files</div>
            <div>defaults</div>
            {Object.keys(initialFilesState.defaults).map((fileStateKey) => {
              return (
                <div className="my-1">
                  <div>{fileStateKey}</div>
                  <div>{initialFilesState.defaults[fileStateKey]}</div>
                </div>
              );
            })}
            <div>initials</div>
            {Object.keys(initialFilesState.initials).map((fileStateKey) => {
              return (
                <div className="my-1">
                  <div>{fileStateKey}</div>
                  <div>{initialFilesState.initials[fileStateKey]}</div>
                </div>
              );
            })}
            <div>current</div>
            {Object.keys(initialFilesState.current).map((fileStateKey) => {
              return (
                <div className="my-1">
                  <div>{fileStateKey}</div>
                  <div>{initialFilesState.current[fileStateKey]}</div>
                </div>
              );
            })}
          </div>
          <div className="flex-grow text-xs" style={{ flexBasis: 1 }}>
            <div>Current</div>
            <div>Defaults</div>
            <div>{currentVersionDefaultValues}</div>
            <div>Files</div>
            <div>defaults</div>
            {Object.keys(currentVersionDefaultFilesState.defaults).map((fileStateKey) => {
              return (
                <div className="my-1">
                  <div>{fileStateKey}</div>
                  <div>{currentVersionDefaultFilesState.defaults[fileStateKey]}</div>
                </div>
              );
            })}
            <div>initials</div>
            {Object.keys(currentVersionDefaultFilesState.initials).map((fileStateKey) => {
              return (
                <div className="my-1">
                  <div>{fileStateKey}</div>
                  <div>{currentVersionDefaultFilesState.initials[fileStateKey]}</div>
                </div>
              );
            })}
            <div>current</div>
            {Object.keys(currentVersionDefaultFilesState.current).map((fileStateKey) => {
              return (
                <div className="my-1">
                  <div>{fileStateKey}</div>
                  <div>{currentVersionDefaultFilesState.current[fileStateKey]}</div>
                </div>
              );
            })}
          </div>
          <div className="flex-grow text-xs" style={{ flexBasis: 1 }}>
            <div>Upgrade</div>
            <div>Defaults</div>
            <div>{upgradeVersionDefaultValues}</div>
            <div>Files</div>
            <div>defaults</div>
            {Object.keys(upgradeVersionDefaultFilesState.defaults).map((fileStateKey) => {
              return (
                <div className="my-1">
                  <div>{fileStateKey}</div>
                  <div>{upgradeVersionDefaultFilesState.defaults[fileStateKey]}</div>
                </div>
              );
            })}
            <div>initials</div>
            {Object.keys(upgradeVersionDefaultFilesState.initials).map((fileStateKey) => {
              return (
                <div className="my-1">
                  <div>{fileStateKey}</div>
                  <div>{upgradeVersionDefaultFilesState.initials[fileStateKey]}</div>
                </div>
              );
            })}
            <div>current</div>
            {Object.keys(upgradeVersionDefaultFilesState.current).map((fileStateKey) => {
              return (
                <div className="my-1">
                  <div>{fileStateKey}</div>
                  <div>{upgradeVersionDefaultFilesState.current[fileStateKey]}</div>
                </div>
              );
            })}
          </div>
          <div className="flex-grow text-xs" style={{ flexBasis: 1 }}>
            <div>Actual</div>
            <div>Values</div>
            <div>{values}</div>
            <div>Files</div>
            <div>defaults</div>
            {Object.keys(filesState.defaults).map((fileStateKey) => {
              return (
                <div className="my-1">
                  <div>{fileStateKey}</div>
                  <div>{filesState.defaults[fileStateKey]}</div>
                </div>
              );
            })}
            <div>initials</div>
            {Object.keys(filesState.initials).map((fileStateKey) => {
              return (
                <div className="my-1">
                  <div>{fileStateKey}</div>
                  <div>{filesState.initials[fileStateKey]}</div>
                </div>
              );
            })}
            <div>current</div>
            {Object.keys(filesState.current).map((fileStateKey) => {
              return (
                <div className="my-1">
                  <div>{fileStateKey}</div>
                  <div>{filesState.current[fileStateKey]}</div>
                </div>
              );
            })}
          </div>
        </div> */}

        {release.version === upgradeVersion ? (
          <>
            <NGLabel>Values</NGLabel>
            <div className="text-sm">
              <CodeEditor language="yaml" options={{}} value={values} setValue={setValues} />
            </div>
          </>
        ) : (
          <>
            <div className="flex gap-2 items-start text-sm">
              <NGLabel className="flex-grow">Current Values</NGLabel>
              <NGLabel className="flex-grow">New Values</NGLabel>
            </div>
            <div className="flex gap-2 items-start text-sm">
              <CodeEditor
                language="yaml"
                options={{ readOnly: true, style: { flexBasis: 1, flexGrow: 1 } }}
                value={initialValues}
                setValue={() => {}}
              />
              <CodeEditor
                language="yaml"
                options={{ style: { flexBasis: 1, flexGrow: 1 } }}
                value={values}
                setValue={setValues}
              />
            </div>
          </>
        )}

        <>
          {release.version === upgradeVersion
            ? placeholder.map((_) => {
                const templateVersion = getTemplateVersion(template, release.version);
                if (!templateVersion) return null;
                return (
                  <div key={`same`} className="mt-4">
                    {Object.keys(templateVersion.files || {}).length < 1 ? null : (
                      <>
                        {Object.entries(templateVersion.files || {}).map(([fileProperty, fileInfo]) => {
                          if (fileInfo.type === "multi") {
                            return null;
                          }

                          const content = filesState.current[fileProperty];

                          return (
                            <div className="my-4">
                              {content ? (
                                <div>
                                  <NGLabel>{fileInfo.label}</NGLabel>
                                  <NGButton
                                    variant={"secondary"}
                                    onClick={() => setCodeEditor({ readonly: false, property: fileProperty })}
                                  >
                                    Edit
                                  </NGButton>
                                </div>
                              ) : (
                                <div>
                                  <NGLabel>{fileInfo.label}</NGLabel>
                                  <NGButton
                                    className="mt-2"
                                    variant="primary"
                                    // TODO make this more clear, let it know which value to update
                                    onClick={() => setCodeEditor({ readonly: false, property: fileProperty })}
                                  >
                                    Set Data
                                  </NGButton>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </>
                    )}
                  </div>
                );
              })
            : null}
          {release.version !== upgradeVersion
            ? placeholder.map((_) => {
                const currentTemplateVersion = getTemplateVersion(template, release.version);
                if (!currentTemplateVersion) return null;

                const upgradeTemplateVersion = getTemplateVersion(template, upgradeVersion);
                if (!upgradeTemplateVersion) return null;

                const currentFileCount = Object.keys(currentTemplateVersion.files || {}).length;
                const upgradeFileCount = Object.keys(upgradeTemplateVersion.files || {}).length;

                return (
                  <div key={`different`} className="mt-4">
                    {currentFileCount < 1 && upgradeFileCount < 1 ? null : (
                      <div className="flex items-center gap-2">
                        <NGLabel size={2} className={`${currentFileCount < 1 ? "invisible" : ""} flex-grow`}>
                          Old Files
                        </NGLabel>
                        <NGLabel size={2} className={`${upgradeFileCount < 1 ? "invisible" : ""} flex-grow`}>
                          New Files
                        </NGLabel>
                      </div>
                    )}
                    <div className="flex items-center">
                      {Object.keys(currentTemplateVersion.files || {}).length < 1 ? null : (
                        <div className="flex-grow">
                          {Object.entries(currentTemplateVersion.files || {}).map(([fileProperty, fileInfo]) => {
                            if (fileInfo.type === "multi") {
                              return null;
                            }

                            const content = initialFilesState.initials[fileProperty];

                            return (
                              <div className="my-4">
                                <NGLabel>{fileInfo.label}</NGLabel>
                                {/* // TODO make this modal only show the data without editing */}
                                <NGButton
                                  variant={"secondary"}
                                  onClick={() => setCodeEditor({ readonly: true, property: fileProperty })}
                                >
                                  View
                                </NGButton>
                              </div>
                            );
                          })}
                        </div>
                      )}
                      {Object.keys(upgradeTemplateVersion.files || {}).length < 1 ? null : (
                        <div className="flex-grow">
                          {Object.entries(upgradeTemplateVersion.files || {}).map(([fileProperty, fileInfo]) => {
                            if (fileInfo.type === "multi") {
                              return null;
                            }

                            const content = filesState.current[fileProperty];

                            return (
                              <div className="my-4">
                                {content ? (
                                  <div>
                                    <NGLabel>{fileInfo.label}</NGLabel>
                                    <NGButton
                                      variant={"secondary"}
                                      onClick={() => setCodeEditor({ readonly: false, property: fileProperty })}
                                    >
                                      Edit
                                    </NGButton>
                                  </div>
                                ) : (
                                  <div>
                                    <NGLabel>{fileInfo.label}</NGLabel>
                                    <NGButton
                                      className="mt-2"
                                      variant="primary"
                                      // TODO make this more clear, let it know which value to update
                                      onClick={() => setCodeEditor({ readonly: false, property: fileProperty })}
                                    >
                                      Set Data
                                    </NGButton>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })
            : null}
        </>
        {release.version !== upgradeVersion ? (
          <NGAlert
            type="warning"
            title="IMPORTANT: Before upgrading to a new version, test the upgrade in a non production environment to ensure correctness. Without proper testing you risk data loss."
          />
        ) : null}
        <div className="mt-4 flex items-center">
          <NGButton
            style={{ width: 220, marginRight: 10 }}
            disabled={(!filesAreDirty && values === initialValues) || isLoadingUpgrade || isLoadingTemplate}
            variant={"danger"}
            outlined
            onClick={() => {
              if (release.version !== upgradeVersion) {
                setUpgradeVersion(release.version);
              } else {
                processDefaults();
              }
            }}
          >
            Reset
          </NGButton>
          <NGButton
            style={{ width: 220, marginRight: 10 }}
            // TODO we can have a way of validating the values
            disabled={isLoadingUpgrade || isLoadingTemplate}
            loading={isLoadingTemplate}
            variant={"primary"}
            onClick={() => handleTemplate()}
          >
            Template
          </NGButton>
          <NGButton
            style={{ width: 220 }}
            disabled={isLoadingUpgrade || isLoadingTemplate}
            loading={isLoadingUpgrade}
            variant={"action"}
            onClick={() => handleUpgrade()}
          >
            Upgrade App
          </NGButton>
        </div>
      </div>
      <div className={`mt-4 p-4 border`}>
        <p>
          <a
            href={`${MARKETPLACE_TEMPLATE_REPO}/${MARKETPLACE_BRANCH}/${release.template}/versions/${upgradeVersion}`}
            target="_blank"
            className="color-link"
          >
            Download the template repo
          </a>
          , navigate to the template folder and update the <span className="font-bold">values.yaml</span> file, then
          install via terminal using the{" "}
          <a href={`${DOCS_URL}/guides/cpln-helm#install-a-release`} target="_blank" className="color-link">
            Helm Install
          </a>{" "}
          CLI command.
        </p>
        <CodeSnippet
          code={`cpln helm upgrade ${
            release.name || "{name}"
          } . -f values.yaml --tag cpln/marketplace=true --tag cpln/marketplace-gvc=${
            release.gvc || "{gvc}"
          } --tag cpln/marketplace-template=${
            release.template
          } --tag cpln/marketplace-template-version=${upgradeVersion} --org ${ConsoleContext.org} --gvc ${
            release.gvc || "{gvc}"
          }`}
          className="mt-3 text-sm"
        />
      </div>
      {codeEditor === null ? null : (
        <CodeEditorModal
          title={getTemplateVersion(template, upgradeVersion)?.files?.[codeEditor.property]?.label || "File"}
          initialValue={
            codeEditor.readonly
              ? initialFilesState.initials[codeEditor.property]
              : filesState.current[codeEditor.property] || ""
          }
          open={codeEditor !== null}
          readonly={codeEditor.readonly}
          onOk={(value) => {
            filesDispatch({
              type: FilesReducerActionType.SET_FILE,
              payload: { content: value, property: codeEditor.property },
            });
            setCodeEditor(null);
          }}
          onClose={() => setCodeEditor(null)}
        />
      )}
      {isTemplateView ? (
        <Modal
          open={isTemplateView}
          width={"90vw"}
          styles={{ body: { height: "70vh", padding: 0 } }}
          onCancel={() => setIsTemplateView(false)}
          maskClosable={false}
          closable={false}
          destroyOnClose={true}
          footer={
            <div className="modal-actions">
              <NGButton variant="secondary" onClick={() => setIsTemplateView(false)} disabled={isLoadingUpgrade}>
                Close
              </NGButton>
              <NGButton
                variant="primary"
                onClick={() => handleUpgrade()}
                loading={isLoadingUpgrade}
                disabled={isLoadingUpgrade}
              >
                Install
              </NGButton>
            </div>
          }
        >
          <div className="h-full overflow-auto p-1">
            <div className="flex items-center gap-2 mb-4">
              <NGLabel>{helmTemplates.length} Items</NGLabel>
              <div className="flex-grow" />
              <NGButton
                onClick={() => {
                  navigator.clipboard.writeText(jsYaml.dump(helmTemplates, { sortKeys: k8sKeySort }));
                  notification.success({ message: "Copied all to clipboard" });
                }}
                variant="secondary"
                size={"small"}
                renderIcon={(_, props) => <Copy {...props} />}
              >
                Copy
              </NGButton>
              <NGButton
                onClick={() => {
                  notification.success({ message: "Download initiated" });
                  const blob = jsYaml.dump(helmTemplates, { sortKeys: k8sKeySort });
                  const file = new Blob([blob], { type: `text/yaml` });
                  const href = URL.createObjectURL(file);
                  const a = document.createElement("a");
                  a.style.display = "none";
                  a.classList.add("cpln-temp-a");
                  a.download = `marketplace-${template.name}-items.yaml`;
                  a.href = href;
                  a.click();
                }}
                variant="secondary"
                size={"small"}
                renderIcon={(_, props) => <Download {...props} />}
              >
                Download
              </NGButton>
            </div>
            <CodeEditor
              language="yaml"
              options={{
                readOnly: true,
                style: {
                  height: "calc(100% - 50px)",
                  overflow: "auto",
                },
                className: "text-sm",
              }}
              value={jsYaml.dump(helmTemplates, { sortKeys: k8sKeySort })}
              setValue={() => {}}
              minHeight="calc(100% - 50px)"
            />
          </div>
        </Modal>
      ) : null}
    </NGFormContext.Provider>
  );
};
